import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShieldCheck, Upload, FileText, Mic, Camera, CheckCircle, XCircle,
  ArrowRight, ArrowLeft, Loader2, AlertCircle, RefreshCw, Trophy,
  Clock, Video, PlayCircle, Send, Sparkles, Download, Maximize2, AlertTriangle,
  TrendingUp, Target, MessageCircle, Zap, BookOpen, Compass, Code2, ListChecks
} from 'lucide-react';
import VerifiedBadge from '../components/VerifiedBadge';
import VerifiedInterviewCoding from '../components/VerifiedInterviewCoding';
import VerifiedInterviewMCQ from '../components/VerifiedInterviewMCQ';
import VerifiedOpportunities from '../components/VerifiedOpportunities';
import LLMHealthBanner from '../components/LLMHealthBanner';
import { loginUrlWithRedirect, getFrontendUrl } from '../utils/urlConfig';

const API = process.env.REACT_APP_BACKEND_URL;
const STEPS = ['Status', 'Resume', 'AI Interview', 'Result'];

const TOTAL_INTERVIEW_SECONDS = 45 * 60; // 45-minute hard cap
const QUESTION_SECONDS = 120; // per-question response time limit
const fmtTime = (s) => `${Math.floor(Math.max(0, s) / 60)}:${String(Math.max(0, s) % 60).padStart(2, '0')}`;

const CATEGORY_LABELS = {
  technical_knowledge: 'Technical Knowledge',
  problem_solving: 'Problem Solving',
  coding_ability: 'Coding Ability',
  analytical_thinking: 'Analytical Thinking',
  communication: 'Communication',
  practical_application: 'Practical Application',
  behavioural_readiness: 'Behavioural Readiness',
  learning_agility: 'Learning Agility',
  resume_claim_validation: 'Resume Claim Validation',
  overall_employability: 'Overall Employability',
  technical_depth: 'Technical Depth',
  confidence: 'Confidence',
  relevance: 'Relevance',
};

const DECISION_MAP = {
  verified: { label: 'Verified', cls: 'text-emerald-300 border-emerald-400/40 bg-emerald-500/10', Icon: CheckCircle },
  provisional: { label: 'Provisionally Verified', cls: 'text-amber-300 border-amber-400/40 bg-amber-500/10', Icon: ShieldCheck },
  reassessment: { label: 'Re-assessment Recommended', cls: 'text-indigo-300 border-indigo-400/40 bg-indigo-500/10', Icon: RefreshCw },
  not_verified: { label: 'Not Verified', cls: 'text-red-300 border-red-400/40 bg-red-500/10', Icon: AlertTriangle },
};

const LEVEL_TONE = {
  Strong: 'text-emerald-300 border-emerald-400/30 bg-emerald-500/10',
  Intermediate: 'text-sky-300 border-sky-400/30 bg-sky-500/10',
  Moderate: 'text-amber-300 border-amber-400/30 bg-amber-500/10',
  'Needs Improvement': 'text-red-300 border-red-400/30 bg-red-500/10',
};

const CLAIM_TONE = {
  'Claim Verified': 'text-emerald-300 bg-emerald-500/10 border-emerald-400/30',
  'Partially Demonstrated': 'text-amber-300 bg-amber-500/10 border-amber-400/30',
  'Insufficient Evidence': 'text-red-300 bg-red-500/10 border-red-400/30',
  'Requires Further Review': 'text-indigo-300 bg-indigo-500/10 border-indigo-400/30',
};

const EmployabilityScorecard = ({ feedback, threshold }) => {
  if (!feedback) return null;
  const scores = feedback.scores || {};
  const scoreKeys = Object.keys(scores);
  const decision = DECISION_MAP[feedback.verification_recommendation];
  const skills = Array.isArray(feedback.skill_competency) ? feedback.skill_competency : [];
  const claims = Array.isArray(feedback.claim_validation) ? feedback.claim_validation : [];
  const roles = Array.isArray(feedback.recommended_roles) ? feedback.recommended_roles : [];
  const th = Number(threshold || 30);
  const hasRich = decision || skills.length || claims.length || roles.length || scoreKeys.length;
  if (!hasRich) return null;
  return (
    <div className="mt-8 space-y-5 text-left" data-testid="employability-scorecard">
      {decision && (
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4">
          <div className="flex items-center gap-2 text-[11px] font-bold tracking-[0.2em] uppercase text-zinc-400">
            <Sparkles size={14} className="text-amber-300" /> Verification decision
          </div>
          <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-sm font-bold ${decision.cls}`} data-testid="verification-decision">
            <decision.Icon size={15} /> {decision.label}
          </div>
        </div>
      )}
      {feedback.recommendation_reason && (
        <p className="max-w-3xl mx-auto text-xs text-zinc-400 leading-relaxed text-center">{feedback.recommendation_reason}</p>
      )}

      {scoreKeys.length > 0 && (
        <div className="max-w-3xl mx-auto rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="employability-categories">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={14} className="text-indigo-300" />
            <h3 className="text-[11px] font-bold tracking-[0.2em] uppercase text-zinc-300">Employability scorecard</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            {scoreKeys.map((k) => {
              const sv = Math.max(0, Math.min(100, Number(scores[k]) || 0));
              const tone = sv >= th ? 'bg-emerald-400' : sv >= th - 15 ? 'bg-amber-400' : 'bg-indigo-400';
              return (
                <div key={k} className="min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-zinc-200">{CATEGORY_LABELS[k] || k.replace(/_/g, ' ')}</span>
                    <span className="text-xs font-bold tabular-nums text-white">{Math.round(sv)}<span className="text-zinc-500">/100</span></span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div className={`h-full ${tone} transition-all`} style={{ width: `${sv}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {skills.length > 0 && (
        <div className="max-w-3xl mx-auto rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="skill-competency">
          <div className="flex items-center gap-2 mb-3">
            <Zap size={14} className="text-amber-300" />
            <h3 className="text-[11px] font-bold tracking-[0.2em] uppercase text-zinc-300">Skill-wise competency</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {skills.map((s, i) => (
              <span key={i} title={s.evidence || ''} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold ${LEVEL_TONE[s.level] || 'text-zinc-300 border-white/15 bg-white/5'}`}>
                {s.skill}<span className="opacity-70">· {s.level}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {claims.length > 0 && (
        <div className="max-w-3xl mx-auto rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="claim-validation">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck size={14} className="text-sky-300" />
            <h3 className="text-[11px] font-bold tracking-[0.2em] uppercase text-zinc-300">Resume claim validation</h3>
          </div>
          <div className="space-y-2.5">
            {claims.map((c, i) => (
              <div key={i} className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-zinc-200 leading-snug">{c.claim}</p>
                  {c.note && <p className="text-[11px] text-zinc-500 leading-snug">{c.note}</p>}
                </div>
                <span className={`flex-shrink-0 px-2.5 py-1 rounded-full border text-[10px] font-bold uppercase tracking-wide ${CLAIM_TONE[c.status] || 'text-zinc-300 bg-white/5 border-white/15'}`}>{c.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {roles.length > 0 && (
        <div className="max-w-3xl mx-auto rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="recommended-roles">
          <div className="flex items-center gap-2 mb-3">
            <Target size={14} className="text-emerald-300" />
            <h3 className="text-[11px] font-bold tracking-[0.2em] uppercase text-zinc-300">Recommended roles</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {roles.map((r, i) => (
              <span key={i} className="inline-flex items-center px-3 py-1.5 rounded-lg border border-white/15 bg-white/5 text-xs font-semibold text-zinc-200">{r}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const VerifiedTalentVerifyPage = () => {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const token = localStorage.getItem('codearena_token');

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [resumeUrl, setResumeUrl] = useState(null);
  const [resumeName, setResumeName] = useState('');
  const [resumeText, setResumeText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [attemptId, setAttemptId] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [sending, setSending] = useState(false);
  const [ending, setEnding] = useState(false);
  const [result, setResult] = useState(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [recording, setRecording] = useState(false);
  const [ttsPlaying, setTtsPlaying] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [violations, setViolations] = useState(0);
  const [violationWarning, setViolationWarning] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [activeActivity, setActiveActivity] = useState(null); // 'coding' | 'mcq' | null
  const [codingDone, setCodingDone] = useState(false);
  const [mcqDone, setMcqDone] = useState(false);
  const interviewActiveRef = useRef(false);
  const autoSubmittedRef = useRef(false);
  const resultShownRef = useRef(false);
  const [totalTimeLeft, setTotalTimeLeft] = useState(null);
  const [questionTimeLeft, setQuestionTimeLeft] = useState(null);
  const [timings, setTimings] = useState({ totalSeconds: TOTAL_INTERVIEW_SECONDS, questionSeconds: QUESTION_SECONDS, codingSeconds: 600, mcqSeconds: 300 });
  const endingRef = useRef(false);
  const interviewRootRef = useRef(null);
  const videoRef = useRef(null);
  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);
  const audioElRef = useRef(null);
  const playedIntroRef = useRef(false);

  useEffect(() => {
    if (!token) {
      navigate(loginUrlWithRedirect('/codearena/auth'), { replace: true });
      return;
    }
    fetchMe();
    // Load admin-configured interview timings
    axios.get(`${API}/api/verified-talent/config`).then((r) => {
      const t = r.data?.timings;
      if (t) setTimings({
        totalSeconds: (Number(t.total_interview_minutes) || 45) * 60,
        questionSeconds: Number(t.question_seconds) || 120,
        codingSeconds: Number(t.coding_seconds) || 600,
        mcqSeconds: Number(t.mcq_seconds) || 300,
      });
    }).catch(() => {});
    // Verify plan payment if order_id in URL
    const orderId = search.get('order_id');
    if (orderId) verifyOrder(orderId);
    // Verify extra-attempts payment if extra_order_id in URL
    const extraOrderId = search.get('extra_order_id');
    if (extraOrderId) verifyExtraOrder(extraOrderId);
    /* eslint-disable-next-line */
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchMe = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/verified-talent/me`, { headers: { Authorization: `Bearer ${token}` } });
      setMe(res.data);
      if (res.data?.profile?.is_verified) {
        setStep(3);
        let best = null;
        try {
          const ar = await axios.get(`${API}/api/verified-talent/attempts`, { headers: { Authorization: `Bearer ${token}` } });
          const completed = (ar.data?.attempts || []).filter((a) => a.status === 'completed' && a.feedback);
          completed.sort((a, b) => (Number(b.score) || 0) - (Number(a.score) || 0));
          best = completed[0] || null;
        } catch { /* non-blocking: show result without historical feedback */ }
        const rem = Math.max(0, Number(res.data.profile.attempts_assigned || 0) - Number(res.data.profile.attempts_used || 0));
        const bestFeedback = best?.feedback || null;
        // A manual admin override takes precedence over the AI recommendation
        if (bestFeedback && res.data.profile.manual_decision) {
          bestFeedback.verification_recommendation = res.data.profile.manual_decision;
        }
        setResult({
          passed: true,
          score: best?.score ?? res.data.profile.best_score,
          threshold: res.data.profile.threshold,
          alreadyVerified: true,
          attempts_remaining: rem,
          feedback: bestFeedback,
        });
      } else if (res.data?.has_active_plan && !resultShownRef.current) {
        setStep(1);
      }
    } catch {
      toast.error('Failed to load status');
    } finally {
      setLoading(false);
    }
  };

  const verifyExtraOrder = async (orderId) => {
    try {
      const res = await axios.post(
        `${API}/api/verified-talent/extra-attempts/verify-payment`,
        { order_id: orderId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.status === 'completed') {
        toast.success(`${res.data.credited_attempts || ''} extra attempt(s) credited!`);
        await fetchMe();
      } else {
        toast.info(`Payment status: ${res.data.order_status || 'pending'}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Extra-attempts verification failed');
    }
  };

  const verifyOrder = async (orderId) => {
    try {
      const res = await axios.post(
        `${API}/api/verified-talent/verify-payment`,
        { order_id: orderId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.status === 'completed') {
        toast.success('Payment verified! Your plan is active.');
        await fetchMe();
        setStep(1);
      } else {
        toast.info(`Payment status: ${res.data.order_status || 'pending'}. Please complete checkout.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Payment verification failed');
    }
  };

  // -------- Resume Upload --------
  const handleResumeUpload = async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be under 10MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/api/mock-interview/upload-resume`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Mock-interview endpoint returns { resume_id, filename, resume_text, text_length }
      setResumeUrl(res.data.resume_id || res.data.filename);
      setResumeText(res.data.resume_text || '');
      setResumeName(file.name);
      toast.success('Resume uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Resume upload failed');
    } finally {
      setUploading(false);
    }
  };

  // -------- Camera toggle --------
  const toggleCamera = async () => {
    if (cameraOn) {
      if (videoRef.current?.srcObject) {
        videoRef.current.srcObject.getTracks().forEach(t => t.stop());
        videoRef.current.srcObject = null;
      }
      setCameraOn(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraOn(true);
    } catch {
      toast.error('Camera permission denied — the interview still works without video');
    }
  };

  // -------- Voice: TTS playback helper --------
  const playTtsAudio = (base64) => {
    if (!base64 || !voiceEnabled) return;
    try {
      if (audioElRef.current) {
        audioElRef.current.pause();
        audioElRef.current.src = '';
      }
      const audio = new Audio(`data:audio/mp3;base64,${base64}`);
      audioElRef.current = audio;
      setTtsPlaying(true);
      audio.onended = () => setTtsPlaying(false);
      audio.onerror = () => setTtsPlaying(false);
      audio.play().catch(() => setTtsPlaying(false));
    } catch {
      setTtsPlaying(false);
    }
  };

  const fetchAndPlayTts = async (text, sid = null) => {
    const effectiveSid = sid || sessionId;
    if (!text || !voiceEnabled || !effectiveSid) return;
    try {
      const res = await axios.post(
        `${API}/api/mock-interview/sessions/${effectiveSid}/tts`,
        { text },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      playTtsAudio(res.data?.audio_base64);
    } catch { /* silent fallback — text still visible */ }
  };

  // -------- Voice: recording --------
  const startRecording = async () => {
    if (recording || sending) return;
    // Stop any ongoing TTS before recording
    if (audioElRef.current) { audioElRef.current.pause(); audioElRef.current = null; }
    setTtsPlaying(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : (MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '');
      const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      audioChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); };
      rec.onstop = handleRecordingStop;
      mediaRecorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      toast.error('Mic access denied — you can still type your answer below');
    }
  };

  const stopRecording = () => {
    const rec = mediaRecorderRef.current;
    if (!rec) return;
    try { if (rec.state !== 'inactive') rec.stop(); } catch { /* noop */ }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(t => t.stop());
      audioStreamRef.current = null;
    }
    setRecording(false);
  };

  const handleRecordingStop = async () => {
    const chunks = audioChunksRef.current;
    audioChunksRef.current = [];
    if (!chunks.length || !sessionId) return;
    const blob = new Blob(chunks, { type: chunks[0].type || 'audio/webm' });
    if (blob.size < 1500) {
      toast.info('Too short — hold the button and speak for a couple of seconds');
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      const ext = (blob.type.includes('webm') ? 'webm' : (blob.type.includes('mp4') ? 'mp4' : 'webm'));
      fd.append('audio', blob, `answer.${ext}`);
      const res = await axios.post(
        `${API}/api/mock-interview/sessions/${sessionId}/voice-message`,
        fd,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const userMsg = res.data?.user_message;
      const aiMsg = res.data?.ai_message;
      if (userMsg) {
        setMessages(prev => [...prev, { role: 'user', content: userMsg.content, is_voice: true, timestamp: userMsg.created_at }]);
      }
      if (aiMsg?.content) {
        const { clean, activity } = processAssistantContent(aiMsg.content);
        if (clean) {
          setMessages(prev => [...prev, { role: 'assistant', content: clean, is_voice: true, timestamp: aiMsg.created_at }]);
        }
        // Avoid speaking the raw marker token: if a marker was present, re-fetch clean TTS
        if (activity) {
          if (voiceEnabled) fetchAndPlayTts(clean);
        } else if (aiMsg.audio_base64) {
          playTtsAudio(aiMsg.audio_base64);
        } else if (voiceEnabled) {
          fetchAndPlayTts(clean);
        }
        maybeTriggerActivity(activity);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not process your answer');
    } finally {
      setSending(false);
    }
  };

  // -------- Start attempt --------
  const startAttempt = async () => {
    try {
      // Enter fullscreen NOW (must be user-gesture) before async network call
      await requestInterviewFullscreen();
      const res = await axios.post(
        `${API}/api/verified-talent/attempts/start`,
        { resume_url: resumeUrl, resume_text: resumeText, resume_filename: resumeName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAttemptId(res.data.attempt_id);
      setSessionId(res.data.session_id);
      setViolations(0);
      setViolationWarning('');
      autoSubmittedRef.current = false;
      resultShownRef.current = false;
      setTotalTimeLeft(timings.totalSeconds);
      setStep(2);
      // Fetch session's initial message
      await fetchSession(res.data.session_id);
      // Auto-start camera
      toggleCamera();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start interview');
    }
  };

  const fetchSession = async (sid) => {
    try {
      const res = await axios.get(`${API}/api/mock-interview/sessions/${sid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const msgs = res.data.messages || [];
      setMessages(msgs);
      // Auto-play the AI's intro message (first assistant message) once
      if (!playedIntroRef.current && voiceEnabled) {
        const first = msgs.find(m => m.role === 'assistant');
        if (first?.content) {
          playedIntroRef.current = true;
          // small delay so playback isn't killed by a re-render
          setTimeout(() => fetchAndPlayTts(first.content, sid), 400);
        }
      }
    } catch { /* noop */ }
  };

  // -------- In-interview activity markers (coding / MCQ) --------
  const CODING_MARKER = '[[CODING_CHALLENGE]]';
  const MCQ_MARKER = '[[MCQ_ROUND]]';
  const processAssistantContent = (raw) => {
    const src = raw || '';
    let activity = null;
    if (src.includes(CODING_MARKER)) activity = 'coding';
    else if (src.includes(MCQ_MARKER)) activity = 'mcq';
    const clean = src.replace(/\[\[CODING_CHALLENGE\]\]/g, '').replace(/\[\[MCQ_ROUND\]\]/g, '').trim();
    return { clean, activity };
  };
  const maybeTriggerActivity = (activity) => {
    if (activity === 'coding' && !codingDone) setTimeout(() => setActiveActivity('coding'), 700);
    else if (activity === 'mcq' && !mcqDone) setTimeout(() => setActiveActivity('mcq'), 700);
  };
  // Append an AI message (used by handlers + activity follow-ups); strips markers + plays clean TTS
  const appendAiMessage = (rawText, isVoice = false) => {
    const { clean, activity } = processAssistantContent(rawText);
    if (clean) {
      setMessages(prev => [...prev, { role: 'assistant', content: clean, is_voice: isVoice, timestamp: new Date().toISOString() }]);
      if (voiceEnabled) fetchAndPlayTts(clean);
    }
    maybeTriggerActivity(activity);
  };

  const sendMessage = async (override) => {
    const text = ((typeof override === 'string' ? override : userInput) || '').trim();
    if (!text || sending || !sessionId) return;
    setQuestionTimeLeft(null);
    setUserInput('');
    setMessages(prev => [...prev, { role: 'user', content: text, timestamp: new Date().toISOString() }]);
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/api/mock-interview/sessions/${sessionId}/message`,
        { message: text, mode: 'text' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Backend returns { user_message, ai_message, ai_response } — prefer ai_message.content
      const aiText = res.data?.ai_message?.content || res.data?.ai_response || '';
      if (aiText) appendAiMessage(aiText, false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI response failed');
    } finally {
      setSending(false);
    }
  };

  const endInterview = async (opts = {}) => {
    if (!sessionId || !attemptId) return;
    if (endingRef.current) return;
    const { auto = false, reason = '' } = opts;
    if (!auto && messages.filter(m => m.role === 'user').length < 3) {
      if (!window.confirm('You have answered only a few questions. Submitting now may hurt your score. Continue anyway?')) return;
    }
    endingRef.current = true;
    setEnding(true);
    interviewActiveRef.current = false;
    try {
      // 1. End the mock interview session (generates feedback/score)
      const endRes = await axios.post(
        `${API}/api/mock-interview/sessions/${sessionId}/end`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const overall = endRes.data?.feedback?.overall_score ?? 0;
      
      // 2. Submit verification attempt with the score
      const submitRes = await axios.post(
        `${API}/api/verified-talent/attempts/submit`,
        {
          attempt_id: attemptId,
          score: overall,
          feedback: endRes.data?.feedback || {},
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Stop camera
      if (videoRef.current?.srcObject) {
        videoRef.current.srcObject.getTracks().forEach(t => t.stop());
        videoRef.current.srcObject = null;
      }
      setCameraOn(false);
      setResult({
        ...submitRes.data,
        feedback: submitRes.data?.feedback || endRes.data?.feedback || {},
        autoSubmitted: auto,
        autoReason: reason,
      });
      resultShownRef.current = true;
      setStep(3);
      // Exit fullscreen cleanly
      try { if (document.fullscreenElement) await document.exitFullscreen(); } catch {}
      await fetchMe();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit attempt');
    } finally {
      endingRef.current = false;
      setEnding(false);
    }
  };

  // -------- Interview timers (total 45 min + per-question) --------
  const handleQuestionTimeout = () => {
    setQuestionTimeLeft(null);
    if (sending || activeActivity || step !== 2) return;
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant') return;
    const text = userInput.trim();
    sendMessage(text || '(No response provided within the time limit — moving on.)');
  };

  // Total interview countdown → auto-submit at 0
  useEffect(() => {
    if (step !== 2 || totalTimeLeft === null) return;
    if (totalTimeLeft <= 0) { triggerAutoSubmit('45-minute time limit reached'); return; }
    const id = setInterval(() => setTotalTimeLeft(t => (t === null ? t : t - 1)), 1000);
    return () => clearInterval(id);
  }, [step, totalTimeLeft]);

  // Reset per-question timer whenever the AI asks a new question (candidate's turn)
  const lastMsg = messages[messages.length - 1];
  useEffect(() => {
    if (step === 2 && lastMsg && lastMsg.role === 'assistant' && !activeActivity) {
      setQuestionTimeLeft(timings.questionSeconds);
    }
  }, [messages.length, activeActivity]);

  // Per-question countdown (paused during activities / recording / AI thinking)
  useEffect(() => {
    if (step !== 2 || questionTimeLeft === null) return;
    if (activeActivity || recording || sending) return;
    if (questionTimeLeft <= 0) { handleQuestionTimeout(); return; }
    const id = setInterval(() => setQuestionTimeLeft(t => (t === null ? t : t - 1)), 1000);
    return () => clearInterval(id);
  }, [step, questionTimeLeft, activeActivity, recording, sending]);

  // -------- Fullscreen + anti-cheat proctoring --------
  const requestInterviewFullscreen = async () => {
    try {
      const el = interviewRootRef.current || document.documentElement;
      if (el.requestFullscreen) await el.requestFullscreen();
      else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
      setIsFullscreen(true);
    } catch {
      // Some browsers deny without a user gesture — we'll retry on button click below.
    }
  };

  const triggerAutoSubmit = (reason) => {
    if (autoSubmittedRef.current) return;
    autoSubmittedRef.current = true;
    toast.error(`Interview auto-submitted · ${reason}`);
    endInterview({ auto: true, reason });
  };

  const handleViolation = (reason) => {
    if (!interviewActiveRef.current) return;
    setViolations(v => {
      const next = v + 1;
      if (next === 1) {
        setViolationWarning(`Warning: ${reason}. One more violation and the interview will auto-submit.`);
        toast.warning(`Proctoring: ${reason}. This is your only warning.`);
      } else if (next >= 2) {
        setViolationWarning('');
        triggerAutoSubmit(reason);
      }
      return next;
    });
  };

  useEffect(() => {
    if (step !== 2) {
      interviewActiveRef.current = false;
      return undefined;
    }
    interviewActiveRef.current = true;

    const onFsChange = () => {
      const fs = !!document.fullscreenElement;
      setIsFullscreen(fs);
      if (!fs && interviewActiveRef.current) {
        handleViolation('exited fullscreen');
      }
    };
    const onVisibility = () => {
      if (document.hidden && interviewActiveRef.current) {
        handleViolation('left the interview tab');
      }
    };
    const onBlur = () => {
      if (interviewActiveRef.current) handleViolation('switched away from the interview');
    };
    const onBeforeUnload = (e) => {
      if (!interviewActiveRef.current) return undefined;
      e.preventDefault();
      e.returnValue = 'Your interview is still in progress. Leaving will auto-submit it.';
      return e.returnValue;
    };
    const onKeydown = (e) => {
      // Block common escape shortcuts
      const k = (e.key || '').toLowerCase();
      if (k === 'escape') { e.preventDefault(); }
      if ((e.ctrlKey || e.metaKey) && (k === 'w' || k === 't' || k === 'n')) {
        e.preventDefault();
      }
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onBlur);
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('keydown', onKeydown);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('keydown', onKeydown);
    };
    /* eslint-disable-next-line */
  }, [step]);

  // -------- Download Verified Talent PDF --------
  const downloadCertificate = async () => {
    if (downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      const res = await axios.get(`${API}/api/verified-talent/certificate`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `EvalentX_Verified_Talent_${(me?.student?.full_name || 'candidate').replace(/[^A-Za-z0-9]+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Certificate downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not download certificate');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const downloadResume = async () => {
    if (downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      const res = await axios.get(`${API}/api/verified-talent/resume.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `EvalentX_Resume_${(me?.student?.full_name || 'candidate').replace(/[^A-Za-z0-9]+/g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Resume downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not download resume');
    } finally {
      setDownloadingPdf(false);
    }
  };

  // -------- Download Growth Report (failed attempts) --------
  const downloadGrowthReport = async () => {
    if (downloadingPdf) return;
    const aid = result?.attempt_id || attemptId;
    if (!aid) {
      toast.error('No attempt reference — please refresh the page.');
      return;
    }
    setDownloadingPdf(true);
    try {
      const res = await axios.get(`${API}/api/verified-talent/growth-report/${aid}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `EvalentX_Growth_Report_${(me?.student?.full_name || 'candidate').replace(/[^A-Za-z0-9]+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Growth report downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not download growth report');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <Loader2 className="animate-spin text-purple-400" size={32} />
      </div>
    );
  }

  const noPlan = !me?.has_active_plan;
  const attemptsRemaining = me?.profile?.attempts_remaining ?? 0;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="verified-talent-verify-page">
      <LLMHealthBanner />
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8">
        {/* Header + Stepper */}
        <div className="mb-8">
          {step !== 2 && (
            <Link to="/verified-talent" className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white mb-4">
              <ArrowLeft size={12} /> Back to plans
            </Link>
          )}
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2.5 bg-purple-500/10 rounded-lg">
              <ShieldCheck className="text-purple-400" size={22} />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Get Verified</h1>
              <p className="text-xs text-zinc-500">Attempts remaining: <strong className="text-purple-300">{attemptsRemaining}</strong> · Plan: {me?.profile?.plan_name || '—'}</p>
            </div>
          </div>
          <div className="mt-6 flex items-center gap-2">
            {STEPS.map((label, idx) => (
              <React.Fragment key={label}>
                <div className={`flex items-center gap-2 ${idx <= step ? 'text-white' : 'text-zinc-600'}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${idx < step ? 'bg-emerald-500 text-white' : idx === step ? 'bg-purple-600 text-white' : 'bg-zinc-800 text-zinc-500'}`}>
                    {idx < step ? <CheckCircle size={14} /> : idx + 1}
                  </div>
                  <span className="text-xs hidden sm:block font-medium">{label}</span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div className={`flex-1 h-px ${idx < step ? 'bg-emerald-500' : 'bg-zinc-800'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step 0: No active plan */}
        {step === 0 && noPlan && (
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-8 text-center" data-testid="verify-no-plan">
            <AlertCircle className="text-amber-400 mx-auto mb-3" size={32} />
            <h2 className="text-xl font-bold mb-2">No Active Plan</h2>
            <p className="text-sm text-zinc-400 mb-5">You need to purchase a Verified Talent plan to start verification.</p>
            <Link to="/verified-talent" className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-500 rounded-lg text-sm font-semibold">
              View Plans <ArrowRight size={14} />
            </Link>
          </div>
        )}

        {/* Step 1: Resume */}
        {step === 1 && !noPlan && (
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-6 md:p-8">
            <h2 className="text-xl font-bold mb-1">Step 1 · Upload your resume</h2>
            <p className="text-sm text-zinc-400 mb-6">AI will use your resume to tailor the interview questions.</p>
            <label htmlFor="resume-input" className="block border-2 border-dashed border-zinc-700 rounded-xl p-10 text-center hover:border-purple-500/60 hover:bg-purple-500/5 transition-colors cursor-pointer" data-testid="resume-dropzone">
              {resumeUrl ? (
                <div>
                  <FileText className="mx-auto text-emerald-400 mb-3" size={32} />
                  <p className="font-semibold text-emerald-300">{resumeName || 'Resume uploaded'}</p>
                  <p className="text-xs text-zinc-500 mt-1">Click to replace</p>
                </div>
              ) : uploading ? (
                <div>
                  <Loader2 className="animate-spin mx-auto text-purple-400 mb-3" size={32} />
                  <p className="text-sm text-zinc-400">Uploading…</p>
                </div>
              ) : (
                <div>
                  <Upload className="mx-auto text-zinc-500 mb-3" size={32} />
                  <p className="font-semibold mb-1">Drop your PDF here or click to browse</p>
                  <p className="text-xs text-zinc-500">Max 10 MB · PDF only</p>
                </div>
              )}
              <input
                id="resume-input"
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={(e) => handleResumeUpload(e.target.files?.[0])}
                data-testid="resume-upload-input"
              />
            </label>
            <div className="flex items-center justify-between mt-6 flex-wrap gap-3">
              <button
                onClick={() => { setResumeUrl(null); setResumeName(''); startAttempt(); }}
                className="text-xs text-zinc-500 hover:text-zinc-300 underline"
                data-testid="skip-resume-btn"
              >
                Skip — use my existing profile
              </button>
              <button
                disabled={uploading || !resumeUrl}
                onClick={startAttempt}
                className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-500 disabled:bg-zinc-800 disabled:text-zinc-600 rounded-lg text-sm font-semibold transition-colors"
                data-testid="start-interview-btn"
              >
                Start AI Interview <ArrowRight size={14} />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Interview */}
        {step === 2 && (
          <div ref={interviewRootRef} data-testid="interview-root" className="relative bg-[#0A0A0A]">
            {/* Proctoring bar */}
            <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-purple-500/30 bg-purple-500/5 px-4 py-2.5" data-testid="proctor-bar">
              <div className="flex items-center gap-2 text-xs text-purple-200">
                <ShieldCheck size={14} className="text-purple-300" />
                <span className="font-semibold uppercase tracking-wider">Proctored Mode</span>
                <span className="text-zinc-500">· Fullscreen required · Tab switching will auto-submit</span>
              </div>
              <div className="flex items-center gap-2">
                {!isFullscreen && (
                  <button
                    onClick={requestInterviewFullscreen}
                    className="text-[11px] font-semibold px-2.5 py-1 rounded bg-amber-500 text-black hover:bg-amber-400 flex items-center gap-1"
                    data-testid="reenter-fullscreen-btn"
                  >
                    <Maximize2 size={12} /> Re-enter Fullscreen
                  </button>
                )}
                <span className="text-[10px] uppercase tracking-widest text-zinc-500">Violations: <strong className={violations ? 'text-red-300' : 'text-emerald-300'}>{violations}</strong> / 2</span>
              </div>
            </div>

            {/* In-interview activity launcher (the AI opens these automatically; manual fallback here) */}
            <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-2.5" data-testid="activity-launcher">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1">Interview activities</span>
              <button
                onClick={() => setActiveActivity('coding')}
                disabled={codingDone || messages.filter(m => m.role === 'assistant').length === 0}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-purple-500/10 text-purple-200 border border-purple-500/30 hover:bg-purple-500/20 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="launch-coding-btn"
              >
                <Code2 size={13} /> {codingDone ? 'Coding done' : 'Coding Challenge'}
              </button>
              <button
                onClick={() => setActiveActivity('mcq')}
                disabled={mcqDone || messages.filter(m => m.role === 'assistant').length === 0}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-sky-500/10 text-sky-200 border border-sky-500/30 hover:bg-sky-500/20 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="launch-mcq-btn"
              >
                <ListChecks size={13} /> {mcqDone ? 'MCQ done' : 'Quick MCQ'}
              </button>
            </div>

            {activeActivity === 'coding' && attemptId && (
              <VerifiedInterviewCoding
                API={API} token={token} attemptId={attemptId}
                timeLimit={timings.codingSeconds}
                onClose={() => { setActiveActivity(null); setCodingDone(true); }}
                onAiMessage={(text) => appendAiMessage(text, false)}
              />
            )}
            {activeActivity === 'mcq' && attemptId && (
              <VerifiedInterviewMCQ
                API={API} token={token} attemptId={attemptId}
                timeLimit={timings.mcqSeconds}
                onClose={() => { setActiveActivity(null); setMcqDone(true); }}
              />
            )}
            {violationWarning && (
              <div className="mb-4 flex items-start gap-3 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-xs text-red-200" data-testid="violation-warning">
                <AlertTriangle size={14} className="text-red-300 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold">Proctoring warning</p>
                  <p className="text-red-100/80">{violationWarning}</p>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Chat */}
            <div className="lg:col-span-2 bg-zinc-900/60 border border-zinc-800 rounded-2xl flex flex-col" style={{ height: '70vh' }}>
              <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${ttsPlaying ? 'bg-purple-400 animate-pulse' : recording ? 'bg-red-500 animate-pulse' : 'bg-emerald-500 animate-pulse'}`} />
                  <span className="text-xs font-semibold uppercase tracking-wide">
                    {ttsPlaying ? 'Athena is speaking…' : recording ? 'Listening…' : 'Live Interview'}
                  </span>
                  {(() => {
                    const qAsked = messages.filter(m => m.role === 'assistant').length;
                    const qAnswered = Math.min(messages.filter(m => m.role === 'user').length, 6);
                    const total = 6;
                    return (
                      <div className="hidden sm:flex items-center gap-2 pl-3 border-l border-zinc-800">
                        <span className="text-[10px] uppercase tracking-widest text-zinc-500">Q</span>
                        <span className="text-xs font-bold text-white">{Math.min(qAsked, total)}</span>
                        <span className="text-[10px] text-zinc-500">/ {total}</span>
                        <div className="w-24 h-1 bg-zinc-800 rounded-full overflow-hidden" data-testid="interview-progress">
                          <div
                            className="h-full bg-gradient-to-r from-emerald-400 to-purple-400 transition-all duration-500"
                            style={{ width: `${Math.min(100, (qAnswered / total) * 100)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <div className="flex items-center gap-2">
                  {totalTimeLeft !== null && (
                    <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded border ${totalTimeLeft <= 300 ? 'bg-red-500/10 text-red-300 border-red-500/30' : 'bg-zinc-800 text-zinc-300 border-zinc-700'}`} data-testid="total-timer" title="Total interview time remaining">
                      <Clock size={11} /> {fmtTime(totalTimeLeft)}
                    </span>
                  )}
                  {questionTimeLeft !== null && (
                    <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded border ${questionTimeLeft <= 20 ? 'bg-red-500/10 text-red-300 border-red-500/30' : 'bg-purple-500/10 text-purple-200 border-purple-500/30'}`} data-testid="question-timer" title="Time left to answer this question">
                      Q {fmtTime(questionTimeLeft)}
                    </span>
                  )}
                  <button
                    onClick={() => {
                      setVoiceEnabled(v => {
                        const next = !v;
                        if (!next && audioElRef.current) { audioElRef.current.pause(); setTtsPlaying(false); }
                        return next;
                      });
                    }}
                    className={`text-[10px] font-semibold px-2 py-1 rounded border ${voiceEnabled ? 'bg-purple-500/10 text-purple-200 border-purple-500/30' : 'bg-zinc-800 text-zinc-400 border-zinc-700'}`}
                    data-testid="voice-toggle-btn"
                    title={voiceEnabled ? 'Mute AI voice' : 'Unmute AI voice'}
                  >
                    {voiceEnabled ? 'VOICE ON' : 'VOICE OFF'}
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm('End the interview now and submit for scoring? This cannot be undone.')) {
                        endInterview({});
                      }
                    }}
                    disabled={ending}
                    className="text-xs font-semibold px-3 py-1.5 bg-red-500/10 text-red-300 hover:bg-red-500/20 rounded border border-red-500/30 disabled:opacity-50"
                    data-testid="end-interview-btn"
                  >
                    {ending ? 'Submitting…' : 'End & Submit'}
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                {messages.length === 0 && (
                  <div className="text-center py-10 text-xs text-zinc-500">
                    <Loader2 className="animate-spin mx-auto mb-2 text-purple-400" size={16} />
                    Athena is preparing your first question…
                  </div>
                )}
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm ${
                      m.role === 'user'
                        ? 'bg-purple-600 text-white rounded-br-sm'
                        : 'bg-zinc-800 text-zinc-100 rounded-bl-sm'
                    }`}>
                      {m.is_voice && (
                        <div className={`flex items-center gap-1 text-[10px] opacity-70 mb-1 ${m.role === 'user' ? 'text-purple-100' : 'text-zinc-400'}`}>
                          <Mic size={10} /> Voice
                        </div>
                      )}
                      {m.content}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start">
                    <div className="bg-zinc-800 text-zinc-400 px-4 py-2.5 rounded-2xl rounded-bl-sm text-sm flex items-center gap-2">
                      <Loader2 size={12} className="animate-spin" /> Athena is typing…
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
              <div className="border-t border-zinc-800 p-4 space-y-3">
                <div className="flex items-center justify-center gap-3">
                  <button
                    onMouseDown={startRecording}
                    onMouseUp={stopRecording}
                    onMouseLeave={() => { if (recording) stopRecording(); }}
                    onTouchStart={(e) => { e.preventDefault(); startRecording(); }}
                    onTouchEnd={(e) => { e.preventDefault(); stopRecording(); }}
                    disabled={sending || ttsPlaying}
                    className={`relative flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all select-none ${
                      recording
                        ? 'bg-red-600 text-white scale-105 shadow-lg shadow-red-500/30'
                        : ttsPlaying
                          ? 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                          : sending
                            ? 'bg-zinc-800 text-zinc-400 cursor-wait'
                            : 'bg-purple-600 hover:bg-purple-500 text-white'
                    }`}
                    data-testid="push-to-talk-btn"
                  >
                    {recording && (
                      <span className="absolute inset-0 rounded-full bg-red-500/40 animate-ping pointer-events-none" />
                    )}
                    <Mic size={16} className={recording ? 'animate-pulse' : ''} />
                    {sending ? 'Transcribing…' : recording ? 'Release to send' : ttsPlaying ? 'Listen to AI first' : 'Hold to speak'}
                  </button>
                </div>
                <p className="text-center text-[10px] text-zinc-600 uppercase tracking-wider">
                  Press and hold · release to send · keyboard fallback below
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !sending && sendMessage()}
                    placeholder="Or type your answer…"
                    className="flex-1 bg-zinc-950 border border-zinc-800 rounded-lg px-4 py-2 text-xs focus:outline-none focus:border-purple-500"
                    data-testid="interview-input"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={sending || !userInput.trim()}
                    className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 disabled:bg-zinc-900 disabled:text-zinc-600 rounded-lg text-xs font-semibold flex items-center gap-1.5"
                    data-testid="send-message-btn"
                  >
                    <Send size={12} />
                  </button>
                </div>
              </div>
            </div>

            {/* Side panel: camera + tips */}
            <div className="space-y-4">
              <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Camera</span>
                  <button
                    onClick={toggleCamera}
                    className={`text-xs px-2 py-1 rounded ${cameraOn ? 'bg-red-500/20 text-red-300' : 'bg-emerald-500/20 text-emerald-300'}`}
                    data-testid="camera-toggle-btn"
                  >
                    {cameraOn ? 'Turn off' : 'Turn on'}
                  </button>
                </div>
                <div className="aspect-video bg-black rounded-lg overflow-hidden flex items-center justify-center">
                  {cameraOn ? (
                    <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-center text-zinc-600">
                      <Camera size={24} className="mx-auto mb-1 opacity-40" />
                      <p className="text-xs">Camera off</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-4 space-y-2 text-xs">
                <p className="font-semibold text-zinc-300 mb-2 text-sm flex items-center gap-1.5"><Mic size={14} className="text-purple-400" /> Voice interview tips</p>
                <p className="text-zinc-500">· Hold the purple button, speak clearly, release to send.</p>
                <p className="text-zinc-500">· Keep responses to 30–60 seconds — quality over length.</p>
                <p className="text-zinc-500">· Quantify impact (metrics, percentages, scale).</p>
                <p className="text-zinc-500">· Answer at least 5 questions before submitting.</p>
                <p className="text-zinc-500">· Typing also works if your mic is unavailable.</p>
              </div>
            </div>
            </div>
          </div>
        )}

        {/* Step 3: Result */}
        {step === 3 && result && (
          <div
            className={`rounded-2xl p-6 md:p-10 ${result.passed ? 'text-center' : 'text-center md:text-left'} ${
              result.passed
                ? 'bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/30'
                : 'bg-gradient-to-br from-[#0a0a0e] to-[#0e1220] border border-white/10'
            }`}
            data-testid="verify-result"
          >
            {result.autoSubmitted && (
              <div className="mb-5 mx-auto max-w-xl rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-xs text-red-200 flex items-center gap-2" data-testid="auto-submit-notice">
                <AlertTriangle size={14} className="text-red-300" />
                <span>Interview auto-submitted due to proctoring violation{result.autoReason ? ` (${result.autoReason})` : ''}.</span>
              </div>
            )}
            <div className={`${result.passed ? 'w-20 h-20' : 'w-16 h-16'} mx-auto mb-5 rounded-full flex items-center justify-center ${result.passed ? 'bg-emerald-500/20' : 'bg-indigo-500/15 ring-1 ring-indigo-400/30'}`}>
              {result.passed ? <Trophy size={36} className="text-emerald-400" /> : <TrendingUp size={30} className="text-indigo-300" />}
            </div>
            <h2 className={`font-bold mb-3 text-center ${result.passed ? 'text-3xl' : 'text-2xl md:text-3xl'}`} style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {result.passed ? 'Congratulations — you\'re Verified!' : 'Your Growth Report is ready'}
            </h2>
            {result.passed && (
              <div className="flex items-center justify-center gap-6 my-6">
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-widest">Your Score</p>
                  <p className="text-5xl font-bold mt-1 text-emerald-400">{Math.round(result.score || 0)}%</p>
                </div>
                <div className="h-12 w-px bg-zinc-700" />
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-widest">Threshold</p>
                  <p className="text-5xl font-bold text-zinc-500 mt-1">{result.threshold || 30}%</p>
                </div>
              </div>
            )}
            
            <EmployabilityScorecard feedback={result.feedback} threshold={result.threshold} />

            {result.passed ? (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <VerifiedBadge isVerified planCode={me?.profile?.plan_code} size="lg" />
                </div>
                <p className="text-sm text-zinc-400 max-w-md mx-auto">
                  Your verified badge is now active across EvalentX. Expires on <strong className="text-white">{result.badge_expires_at ? new Date(result.badge_expires_at).toLocaleDateString() : '—'}</strong>.
                </p>

                {/* Public profile link callout */}
                {me?.student?.username && (
                  <div
                    className="mt-4 max-w-xl mx-auto rounded-2xl border border-amber-300/30 bg-gradient-to-br from-amber-500/10 via-yellow-500/5 to-amber-700/10 px-4 py-4 text-left"
                    data-testid="verified-public-profile-card"
                  >
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-[0.18em] text-amber-300/80">Your verified public profile</p>
                        <p className="font-mono text-xs sm:text-sm text-white truncate" data-testid="verified-profile-url">
                          {`${getFrontendUrl()}/profile/${me.student.username}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(`${getFrontendUrl()}/profile/${me.student.username}`);
                              toast.success('Profile link copied');
                            } catch {
                              toast.error('Could not copy');
                            }
                          }}
                          className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-white text-xs font-semibold"
                          data-testid="copy-verified-profile-link-btn"
                        >
                          Copy link
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            const url = `${getFrontendUrl()}/profile/${me.student.username}`;
                            const text = `Check out my EvalentX Verified Profile`;
                            if (navigator.share) {
                              try {
                                await navigator.share({ title: 'EvalentX Verified', text, url });
                              } catch { /* user cancelled */ }
                            } else {
                              try {
                                await navigator.clipboard.writeText(url);
                                toast.success('Link copied to share');
                              } catch {
                                toast.error('Could not share');
                              }
                            }
                          }}
                          className="px-3 py-1.5 rounded-lg bg-amber-300 hover:bg-amber-200 text-black text-xs font-bold"
                          data-testid="share-verified-profile-btn"
                        >
                          Share
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-center gap-3 pt-3 flex-wrap">
                  {result.alreadyVerified && result.attempts_remaining > 0 && (
                    <button
                      onClick={() => { setStep(1); setResult(null); setMessages([]); setAttemptId(null); setSessionId(null); }}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-500 rounded-lg text-sm font-bold"
                      data-testid="start-new-assessment-btn"
                    >
                      <RefreshCw size={14} /> Start a new assessment
                    </button>
                  )}
                  <button
                    onClick={downloadCertificate}
                    disabled={downloadingPdf}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-400 hover:bg-amber-300 text-black rounded-lg text-sm font-bold disabled:opacity-60"
                    data-testid="download-certificate-btn"
                  >
                    {downloadingPdf ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                    {downloadingPdf ? 'Preparing PDF…' : 'Download Verified PDF'}
                  </button>
                  <button
                    onClick={downloadResume}
                    disabled={downloadingPdf}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-semibold disabled:opacity-60"
                    data-testid="download-resume-btn"
                  >
                    <FileText size={14} /> Download Resume
                  </button>
                  <Link
                    to={me?.student?.username ? `/profile/${me.student.username}` : '/codearena/dashboard'}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm font-semibold"
                    data-testid="view-profile-btn"
                  >
                    View my public profile <ArrowRight size={14} />
                  </Link>
                  <Link to="/codearena/jobs" className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-semibold">
                    Browse jobs
                  </Link>
                  {me?.profile?.verification_id && (
                    <Link
                      to={`/verify/${me.profile.verification_id}`}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-sm font-semibold"
                      data-testid="view-verified-report-btn"
                    >
                      View verified report <ArrowRight size={14} />
                    </Link>
                  )}
                </div>

                <VerifiedOpportunities token={token} />
              </div>
            ) : (
              <div className="space-y-6 text-left" data-testid="growth-report-panel">
                <div className="text-center">
                  <p className="text-sm text-zinc-300 max-w-xl mx-auto leading-relaxed">
                    You're closer than this number suggests. Below is your <strong className="text-white">personalised growth report</strong> —
                    a forward-looking snapshot of what you did well and the focused actions that will help you clear
                    the Verified Talent bar next time.
                  </p>
                  <p className="text-xs text-zinc-500 mt-2">
                    Attempts remaining:{' '}
                    <strong className={result.attempts_remaining > 0 ? 'text-amber-300' : 'text-red-400'}>
                      {result.attempts_remaining}
                    </strong>
                  </p>
                </div>

                {/* Score band */}
                <div className="grid grid-cols-3 gap-3 max-w-xl mx-auto" data-testid="growth-report-score-band">
                  {(() => {
                    const sc = Math.round(result.score || 0);
                    const th = Math.round(result.threshold || 30);
                    const gap = Math.max(0, th - sc);
                    const cells = [
                      { k: 'Your Score', v: `${sc}`, suffix: '/100', tone: 'text-white' },
                      { k: 'Qualifying', v: `${th}`, suffix: '/100', tone: 'text-zinc-300' },
                      { k: 'Gap to clear', v: `${gap}`, suffix: 'pts', tone: 'text-amber-300' },
                    ];
                    return cells.map((c) => (
                      <div key={c.k} className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                        <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{c.k}</p>
                        <p className={`mt-1 text-3xl font-bold tabular-nums ${c.tone}`} style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                          {c.v}
                          <span className="text-xs font-semibold text-zinc-500 ml-1">{c.suffix}</span>
                        </p>
                      </div>
                    ));
                  })()}
                </div>

                {/* Strengths + Growth areas */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
                  <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.04] p-5" data-testid="growth-strengths">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle size={14} className="text-emerald-300" />
                      <h3 className="text-[11px] font-bold tracking-[0.2em] uppercase text-emerald-200">What you did well</h3>
                    </div>
                    {(result.feedback?.strengths?.length || 0) > 0 ? (
                      <ul className="space-y-2 text-sm text-zinc-200">
                        {result.feedback.strengths.slice(0, 5).map((s, i) => (
                          <li key={i} className="flex gap-2 leading-relaxed"><span className="text-emerald-300 flex-shrink-0">—</span><span>{s}</span></li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-zinc-500 leading-relaxed">
                        Showing up for the interview is itself a strong signal. Use the next attempt to add quantified outcomes to each story.
                      </p>
                    )}
                  </div>
                  <div className="rounded-2xl border border-indigo-500/20 bg-indigo-500/[0.04] p-5" data-testid="growth-focus-areas">
                    <div className="flex items-center gap-2 mb-3">
                      <Target size={14} className="text-indigo-300" />
                      <h3 className="text-[11px] font-bold tracking-[0.2em] uppercase text-indigo-200">Focus for next attempt</h3>
                    </div>
                    {(() => {
                      const rewrite = (t) => (t || '')
                        .replace(/\black(ed|ing)?\s+/gi, 'strengthen ')
                        .replace(/\bweak\s+/gi, 'grow ')
                        .replace(/\bpoor\s+/gi, 'sharpen ')
                        .replace(/\bstruggle(d)?\s+with\s+/gi, 'practice ')
                        .replace(/\bneeds?\s+improvement\s+in\s+/gi, 'deepen ')
                        .replace(/\bimprove\s+on\s+/gi, 'deepen ')
                        .replace(/\bimprove\s+/gi, 'deepen ')
                        .trim();
                      const items = (result.feedback?.improvements || []).map(rewrite).filter(Boolean).slice(0, 5);
                      if (items.length === 0) {
                        return (
                          <ul className="space-y-2 text-sm text-zinc-200">
                            <li className="flex gap-2 leading-relaxed"><span className="text-indigo-300 flex-shrink-0">→</span><span>Deepen clarity of thought — structure answers as Situation → Action → Outcome.</span></li>
                            <li className="flex gap-2 leading-relaxed"><span className="text-indigo-300 flex-shrink-0">→</span><span>Practice communication — keep responses 45–60 seconds with quantified impact.</span></li>
                            <li className="flex gap-2 leading-relaxed"><span className="text-indigo-300 flex-shrink-0">→</span><span>Align resume talking points with the stories you plan to share.</span></li>
                          </ul>
                        );
                      }
                      return (
                        <ul className="space-y-2 text-sm text-zinc-200">
                          {items.map((g, i) => (
                            <li key={i} className="flex gap-2 leading-relaxed"><span className="text-indigo-300 flex-shrink-0">→</span><span>{g.charAt(0).toUpperCase() + g.slice(1)}{g.endsWith('.') ? '' : '.'}</span></li>
                          ))}
                        </ul>
                      );
                    })()}
                  </div>
                </div>

                {/* Encouragement */}
                <div className="max-w-2xl mx-auto rounded-xl border border-white/10 bg-white/[0.02] px-5 py-4 text-center">
                  <p className="text-xs italic text-zinc-400 leading-relaxed">
                    Every strong candidate is built through iteration. This report is a forward-looking tool, not a grade —
                    use it, rehearse, and come back sharper. Recruiters only ever see the <strong className="text-white">verified</strong> outcome.
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-center gap-3 pt-2 flex-wrap">
                  <button
                    onClick={downloadGrowthReport}
                    disabled={downloadingPdf}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-black rounded-lg text-sm font-bold hover:bg-zinc-200 disabled:opacity-60"
                    data-testid="download-growth-report-btn"
                  >
                    {downloadingPdf ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                    {downloadingPdf ? 'Preparing PDF…' : 'Download Growth Report'}
                  </button>
                  {result.attempts_remaining > 0 ? (
                    <button
                      onClick={() => { setStep(1); setResult(null); setMessages([]); setAttemptId(null); setSessionId(null); }}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-500 rounded-lg text-sm font-semibold"
                      data-testid="retry-btn"
                    >
                      <RefreshCw size={14} /> Retry Interview
                    </button>
                  ) : (
                    <>
                      <ExtraAttemptsButton />
                      <Link
                        to="/verified-talent"
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-black rounded-lg text-sm font-semibold"
                      >
                        Upgrade Plan <ArrowRight size={14} />
                      </Link>
                    </>
                  )}
                  <Link to="/codearena/dashboard" className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-semibold">
                    Back to dashboard
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifiedTalentVerifyPage;

// Extra-attempts micro-purchase popover button
const ExtraAttemptsButton = () => {
  const [open, setOpen] = useState(false);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buying, setBuying] = useState(null);
  const token = localStorage.getItem('codearena_token');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API}/api/verified-talent/config`)
      .then(res => setOffers(res.data?.extra_attempt_prices || []))
      .catch(() => toast.error('Could not load extra-attempt offers'))
      .finally(() => setLoading(false));
  }, [open]);

  const loadCashfreeSdk = () =>
    new Promise((resolve) => {
      if (window.Cashfree) return resolve();
      const s = document.createElement('script');
      s.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
      s.onload = resolve;
      document.body.appendChild(s);
    });

  const buy = async (index) => {
    setBuying(index);
    try {
      const res = await axios.post(
        `${API}/api/verified-talent/extra-attempts/purchase`,
        { package_index: index },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { payment_session_id, cashfree_env, order_id } = res.data || {};
      if (!payment_session_id) {
        toast.error('Could not create payment session');
        setBuying(null);
        return;
      }
      await loadCashfreeSdk();
      // eslint-disable-next-line no-undef
      const cashfree = window.Cashfree({ mode: cashfree_env === 'sandbox' ? 'sandbox' : 'production' });
      cashfree.checkout({
        paymentSessionId: payment_session_id,
        redirectTarget: '_self',
        returnUrl: `${window.location.origin}/verified-talent/verify?extra_order_id=${order_id}`,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start checkout');
      setBuying(null);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-500 rounded-lg text-sm font-semibold"
        data-testid="buy-extra-attempts-btn"
      >
        <Sparkles size={14} /> Buy Extra Attempts
      </button>
    );
  }
  return (
    <div className="w-full max-w-md mx-auto bg-zinc-900 border border-purple-500/40 rounded-xl p-5 shadow-2xl" data-testid="extra-attempts-offers">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-bold text-white">Add Extra Attempts</h4>
        <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white">✕</button>
      </div>
      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="animate-spin text-purple-400" size={20} /></div>
      ) : (
        <div className="space-y-2">
          {offers.map((o, i) => (
            <button
              key={i}
              onClick={() => buy(i)}
              disabled={buying !== null}
              className="w-full flex items-center justify-between gap-3 p-3 bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/30 rounded-lg text-left transition-colors disabled:opacity-50"
              data-testid={`extra-attempt-offer-${i}`}
            >
              <div>
                <div className="font-semibold text-white text-sm">{o.label}</div>
                <div className="text-xs text-zinc-400">+{o.attempts} AI interview attempt{o.attempts > 1 ? 's' : ''}</div>
              </div>
              <div className="text-right">
                <div className="text-xl font-bold text-amber-300">₹{o.price}</div>
                {buying === i && <div className="text-[10px] text-zinc-400">Loading…</div>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Import Sparkles separately to avoid conflict (already imported via lucide-react above)
